import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './Support.module.sass';
import InfoPage from '../InfoPage';
import { SUPPORT_ARTICLES, getSupportArticle } from './articles';

// Article copy follows ASD-STE100 Simplified Technical English:
// short sentences, active voice, one instruction per sentence.

const ArticleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </svg>
);

const STEP_IMAGE_WIDTH = 1024;
const STEP_IMAGE_HEIGHT = 662;

const getStepImageSources = (src) => {
  const base = src.replace(/\.(png|jpe?g|webp)$/i, '');
  return {
    png: `${base}.png`,
    webp1x: `${base}.webp`,
    webp2x: `${base}@2x.webp`,
  };
};

const StepImage = ({ src, alt, title }) => {
  const { png, webp1x, webp2x } = getStepImageSources(src);

  return (
    <picture className={styles.stepPicture}>
      <source
        type="image/webp"
        srcSet={`${webp1x} 1x, ${webp2x} 2x`}
      />
      <img
        className={styles.stepImage}
        src={png}
        alt={alt || title}
        width={STEP_IMAGE_WIDTH}
        height={STEP_IMAGE_HEIGHT}
        loading="eager"
        decoding="async"
      />
    </picture>
  );
};

const SupportIndex = ({ navigate }) => (
  <>
    <div className={styles.hero}>
      <h1 className={styles.title}>How can we help?</h1>
      <p className={styles.subtitle}>
        Find answers about your account, your plan, and the platform.
        Select an article below.
      </p>
    </div>

    <div className={styles.divider}>
      <span className={styles.dividerLabel}>Articles</span>
    </div>

    <div className={styles.list}>
      {SUPPORT_ARTICLES.map((article) => (
        <div
          key={article.slug}
          className={styles.articleCard}
          role="link"
          tabIndex={0}
          onClick={() => navigate(`/support/${article.slug}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/support/${article.slug}`);
            }
          }}
        >
          <div className={styles.articleIcon}><ArticleIcon /></div>
          <div className={styles.articleBody}>
            <span className={styles.articleCategory}>{article.category}</span>
            <h2 className={styles.articleTitle}>{article.title}</h2>
            <p className={styles.articleSummary}>{article.summary}</p>
          </div>
          <div className={styles.articleArrow}><ArrowIcon /></div>
        </div>
      ))}
    </div>

    <div className={styles.contact}>
      <p>
        Did you not find an answer? Send an email to{' '}
        <a href="mailto:jamie@postaway.com">jamie@postaway.com</a>.
      </p>
    </div>
  </>
);

const SupportArticle = ({ article, navigate }) => (
  <>
    <button type="button" className={styles.backLink} onClick={() => navigate('/support')}>
      <BackIcon />
      All Articles
    </button>

    <div className={styles.articleHeader}>
      <span className={styles.articleCategory}>{article.category}</span>
      <h1 className={styles.title}>{article.title}</h1>
      <span className={styles.articleMeta}>Updated {article.updated}</span>
    </div>

    {article.intro && <p className={styles.intro}>{article.intro}</p>}

    <div className={styles.steps}>
      {article.steps.map((step, index) => (
        <div key={index} className={styles.step}>
          <div className={styles.stepHeader}>
            <div className={styles.stepNumber}>{index + 1}</div>
            <h2 className={styles.stepTitle}>{step.title}</h2>
          </div>
          <p className={styles.stepText}>{step.text}</p>
          {step.image && (
            <StepImage
              src={step.image}
              alt={step.alt}
              title={step.title}
            />
          )}
        </div>
      ))}
    </div>

    {article.outro && (
      <div className={styles.outro}>
        <p>{article.outro}</p>
      </div>
    )}

    <div className={styles.contact}>
      <p>
        Do you have more questions? Send an email to{' '}
        <a href="mailto:jamie@postaway.com">jamie@postaway.com</a>.
      </p>
    </div>
  </>
);

const Support = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const article = slug ? getSupportArticle(slug) : null;

  // An unknown slug goes back to the index instead of a blank page.
  useEffect(() => {
    if (slug && !article) navigate('/support', { replace: true });
  }, [slug, article, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const content = (
    <div className={styles.page}>
      <div className={styles.board}>
        <div className={styles.inner}>
          {article
            ? <SupportArticle article={article} navigate={navigate} />
            : <SupportIndex navigate={navigate} />}
        </div>
      </div>
    </div>
  );

  return (
    <InfoPage
      title="Support"
      divider={false}
      below={content}
      disableStamps
      tallOverride={false}
      customMinHeight={undefined}
    />
  );
};

export default Support;
